import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

function Login() {
  const { signInWithGoogle, error, loading } = useAuth();
  const [signingIn, setSigningIn] = useState(false);

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle(false);
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setSigningIn(false);
    }
  };

  // Detect problematic browsers
  const detectProblematicBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for Facebook browser
    if (ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1) {
      return 'Facebook';
    }
    
    // Check for Instagram browser
    if (ua.indexOf('Instagram') > -1) {
      return 'Instagram';
    }
    
    // Check for LinkedIn browser
    if (ua.indexOf('LinkedInApp') > -1) {
      return 'LinkedIn';
    }
    
    // Check for Twitter browser
    if (ua.indexOf('Twitter') > -1) {
      return 'Twitter';
    }
    
    // Check for WeChat
    if (ua.indexOf('MicroMessenger') > -1) {
      return 'WeChat';
    }
    
    return null;
  };
  
  const problematicBrowser = detectProblematicBrowser();

  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          {/* Browser Warning */}
          {problematicBrowser && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>⚠️ {problematicBrowser} Browser Detected</strong>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Google sign-in may not work in this browser. Please:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                1. Tap the menu (⋮ or ...)
                <br />
                2. Select "Open in Browser" or "Open in Safari/Chrome"
                <br />
                3. Or copy this link: <strong>{window.location.href}</strong>
              </Typography>
            </Alert>
          )}
          {/* CCRS Logo Area */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src="/ccrs-logo.webp"
              alt="CCRS Logo"
              style={{
                maxWidth: '120px',
                height: 'auto',
                marginBottom: '16px'
              }}
              onError={(e) => {
                // Hide image if it fails to load
                e.target.style.display = 'none';
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Operations Center
            </Typography>
            <Typography variant="body1" color="text.secondary">
              CCRS Fleet & Operations Management
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Sign In Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Sign In to Continue
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use your @craigrescue.com Google account
            </Typography>

            {/* Status Display */}
            {error && (
              <Alert 
                severity={error === 'Determining access permissions...' ? 'info' : 'error'} 
                sx={{ mb: 3 }}
              >
                {error === 'Determining access permissions...'
                  ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        Determining access permissions...
                      </Typography>
                    </Box>
                  )
                  : error.includes('popup-blocked') 
                  ? 'Popup blocked. Please allow popups or use the mobile sign-in option.'
                  : error.includes('cancelled-popup-request')
                  ? 'Sign-in was cancelled. Please try again.'
                  : error.includes('Access denied')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Authentication Failed</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        {error}
                      </Typography>
                    </Box>
                  )
                  : error.includes('secure') || error.includes('disallowed_useragent')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Browser Security Issue Detected</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Google requires a secure browser. Please try:
                      </Typography>
                      <Typography variant="caption" component="ul" sx={{ textAlign: 'left', mt: 1 }}>
                        <li>Open in Chrome, Safari, or Firefox</li>
                        <li>Copy this link: {window.location.href}</li>
                        <li>If in Facebook/Instagram, tap "..." → "Open in Browser"</li>
                        <li>On iPhone: Use Safari instead of app browsers</li>
                      </Typography>
                    </Box>
                  )
                  : error.includes('internal-error')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Authentication Error</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Please try the following:
                      </Typography>
                      <Typography variant="caption" component="ul" sx={{ textAlign: 'left', mt: 0.5, pl: 2 }}>
                        <li>Clear your browser cookies and cache</li>
                        <li>Disable browser extensions temporarily</li>
                        <li>Try opening in an incognito/private window</li>
                        <li>Use a different browser (Chrome, Safari, Firefox)</li>
                      </Typography>
                    </Box>
                  )
                  : `Sign-in error: ${error}`
                }
              </Alert>
            )}

            {/* Google Sign In Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={
                signingIn || loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GoogleIcon />
                )
              }
              onClick={handleGoogleSignIn}
              disabled={signingIn || loading}
              sx={{
                minHeight: '48px',
                backgroundColor: '#4285f4',
                '&:hover': {
                  backgroundColor: '#3367d6',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
              }}
            >
              {signingIn || loading
                ? 'Signing in...'
                : 'Sign in with Google'
              }
            </Button>

            {/* Help Text */}
            <Box sx={{ mt: 4, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Having Trouble Signing In?</strong>
              </Typography>
              
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                <strong>If you see "doesn't comply with secure browser":</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="ul" sx={{ mt: 0.5, pl: 2 }}>
                <li>Open in Chrome, Safari, or Firefox directly</li>
                <li>Don't use app browsers (Facebook, Instagram, etc.)</li>
                <li>On iPhone: Use Safari, not Chrome</li>
                <li>Clear browser cache and cookies</li>
              </Typography>
              
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                <strong>Need Access?</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Contact your administrator to be added to:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                • officers@craigrescue.com (Admin)<br />
                • all@craigrescue.com (Inspector)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Privacy Notice and Footer */}
      <Card sx={{ mt: 2, backgroundColor: 'grey.50' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="error" align="center" display="block" sx={{ fontWeight: 'bold' }}>
            PRIVATE BUSINESS APPLICATION - AUTHORIZED USERS ONLY
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            This is the CCRS Operations Center exclusively for Craig County Rescue - EMS members. 
            This is NOT a public service. Unauthorized access is strictly prohibited and will be reported.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            By logging in, you confirm you are an authorized CCRS member with valid access permissions.
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            © 2025 Craig County Rescue - EMS - Internal Use Only
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;